import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";
import { authAPI, referralAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { Zap, Loader2, Gift, CheckCircle } from "lucide-react";

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  // Check for referral code in URL or localStorage on mount
  useEffect(() => {
    // First check URL params
    const refCode = searchParams.get('ref');
    if (refCode) {
      console.log('[REFERRAL] Found ref code in URL:', refCode);
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
      return;
    }
    
    // If no URL param, check localStorage for persisted referral code
    try {
      const savedReferral = localStorage.getItem('pending_referral');
      if (savedReferral) {
        const referralData = JSON.parse(savedReferral);
        // Check if not expired
        if (referralData.expiresAt > Date.now()) {
          console.log('[REFERRAL] Restored referral code from localStorage:', referralData.code);
          setReferralCode(referralData.code);
          validateReferralCode(referralData.code);
          return;
        } else {
          // Clean up expired referral
          console.log('[REFERRAL] Referral code expired, removing');
          localStorage.removeItem('pending_referral');
        }
      }
      
      // Fallback: check sessionStorage
      const sessionRefCode = sessionStorage.getItem('referral_code');
      if (sessionRefCode) {
        console.log('[REFERRAL] Restored referral code from sessionStorage:', sessionRefCode);
        setReferralCode(sessionRefCode);
        validateReferralCode(sessionRefCode);
        return;
      }
    } catch (e) {
      console.error('[REFERRAL] Error reading storage:', e);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      return;
    }
    try {
      const response = await referralAPI.validateCode(code);
      if (response.data.valid) {
        setReferralValid(true);
        setReferrerName(response.data.referrer_name || "A friend");
      } else {
        setReferralValid(false);
      }
    } catch (error) {
      setReferralValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Include referral code in registration - include it even if validation is pending
      // The backend will validate and handle invalid codes gracefully
      const registrationData = {
        ...formData,
        // Send referral code if present (backend will validate)
        ...(referralCode ? { referral_code: referralCode.toUpperCase() } : {})
      };
      
      console.log('[REFERRAL] Submitting registration with data:', { 
        email: formData.email, 
        hasReferral: !!referralCode,
        referralCode: referralCode || 'none',
        referralValid 
      });
      
      const response = await authAPI.register(registrationData);
      const { access_token, user, referral_bonus } = response.data;
      setAuth(user, access_token);
      
      // Clear persisted referral code after successful registration
      localStorage.removeItem('pending_referral');
      
      if (referral_bonus) {
        toast.success(`Welcome! You've been referred by ${referral_bonus.referrer_name}. You got ${referral_bonus.bonus_credits} bonus credits!`);
      } else if (referralCode && !referral_bonus) {
        // Referral code was provided but didn't result in bonus (invalid code)
        console.log('[REFERRAL] Referral code provided but no bonus received - code may be invalid');
        toast.success("Account created successfully!");
      } else {
        toast.success("Account created successfully!");
      }
      navigate("/dashboard");
    } catch (error) {
      console.error('[REFERRAL] Registration error:', error);
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Force fresh origin read and add cache-busting timestamp
    const origin = window.location.protocol + '//' + window.location.host;
    const timestamp = Date.now();
    const redirectUrl = `${origin}?_t=${timestamp}`;
    console.log('[GOOGLE_SIGNUP] Redirect URL:', redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0B]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#00F0FF]/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#FF0055]/10 rounded-full blur-[128px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-white tracking-tight">iGoViral</h1>
            <p className="text-xs text-muted-foreground">Content Creator</p>
          </div>
        </div>

        <Card className="bg-card/50 border-border/50 backdrop-blur-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold text-white">Create Account</CardTitle>
            <CardDescription>Start creating viral content today</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Referral Banner */}
            {referralValid && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">You've been invited by {referrerName}!</p>
                    <p className="text-green-400 text-sm">Sign up to get 3 free video credits</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-400 ml-auto" />
                </div>
              </motion.div>
            )}

            {/* Google Sign Up */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignup}
              disabled={googleLoading}
              className="w-full h-12 border-border/50 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl gap-3 mb-6"
              data-testid="google-signup-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              Sign up with Google
            </Button>

            <div className="relative mb-6">
              <Separator className="bg-border/50" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                or continue with email
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF]"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="creator@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF]"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF]"
                  data-testid="register-password-input"
                />
              </div>

              {/* Referral Code Input - only show if not already validated */}
              {!referralValid && (
                <div className="space-y-2">
                  <Label htmlFor="referral" className="text-white flex items-center gap-2">
                    Referral Code <Badge variant="outline" className="text-xs">Optional</Badge>
                  </Label>
                  <div className="relative">
                    <Input
                      id="referral"
                      type="text"
                      placeholder="Enter referral code"
                      value={referralCode}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase();
                        setReferralCode(code);
                        if (code.length >= 4) {
                          validateReferralCode(code);
                        } else {
                          setReferralValid(null);
                        }
                      }}
                      className={`h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF] ${
                        referralValid === true ? 'border-green-500' : 
                        referralValid === false ? 'border-red-500' : ''
                      }`}
                      data-testid="register-referral-input"
                    />
                    {referralValid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                    )}
                  </div>
                  {referralValid === false && (
                    <p className="text-red-400 text-xs">Invalid referral code</p>
                  )}
                  {referralValid === true && (
                    <p className="text-green-400 text-xs">Valid code! You'll get 3 free credits.</p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl neon-glow"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-[#00F0FF] hover:underline font-medium">
                Sign in
              </Link>
            </p>
            
            <div className="text-center text-xs text-muted-foreground mt-4 pt-4 border-t border-border/30">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="text-[#00F0FF] hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#00F0FF] hover:underline">
                Privacy Policy
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Copyright */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} iGoViral Content Creator. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
